<script setup lang="ts">
    import { computed, ref } from 'vue';
    import { formatNumber } from '@shared/utils/formatNumber';
    import { vTooltip } from '@shared/directives';
    import { Line } from 'vue-chartjs';
    import { Chart as ChartJS, CategoryScale, Filler, LinearScale, LineElement, PointElement, Tooltip } from 'chart.js';
    import type { ChartData, ChartOptions, Plugin } from 'chart.js';
    import { applyChartDefaults } from '@shared/modules/chartjs-defaults';
    import { chartColor } from '@shared/modules/chart-colors';
    import type { ChartColorToken } from '@shared/modules/chart-colors';
    import InsightsIcon from './InsightsIcon.vue';
    import type { InsightsIconName } from '../insights-icons';

    // Tooltip must be registered before applyChartDefaults(), which sets its defaults
    ChartJS.register(CategoryScale, LinearScale, LineElement, PointElement, Filler, Tooltip);
    applyChartDefaults();

    const props = defineProps<{
        label: string;
        value: string;
        delta: number;
        deltaUnit: '%' | 'pp';
        higherIsBetter: boolean;
        trend: number[];
        trendLabels: string[];
        color: ChartColorToken;
        formatTrendValue: (value: number) => string;
    }>();

    const hoverIndex = ref<number | null>(null);

    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

    // Vertical line through the hovered point
    const crosshairPlugin: Plugin<'line'> = {
        id: 'insightsKpiCrosshair',
        afterDatasetsDraw(chart) {
            const active = chart.getActiveElements()[0];
            if (!active) return;
            const { ctx, chartArea } = chart;
            ctx.save();
            ctx.strokeStyle = chartColor('grid');
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(active.element.x, 0);
            ctx.lineTo(active.element.x, chartArea.bottom);
            ctx.stroke();
            ctx.restore();
        },
    };

    const chartData = computed<ChartData<'line'>>(() => {
        const last = props.trend.length - 1;
        return {
            labels: props.trendLabels,
            datasets: [
                {
                    data: props.trend,
                    borderColor: chartColor(props.color),
                    backgroundColor: chartColor(props.color, 0.1),
                    borderWidth: 2,
                    fill: 'start',
                    clip: false,
                    // Latest month is marked by default
                    pointRadius: props.trend.map((_, i) => (i === last ? 4 : 0)),
                    pointHoverRadius: 4,
                    pointBorderWidth: 2,
                    pointHoverBorderWidth: 2,
                    pointBackgroundColor: chartColor('surface'),
                    pointBorderColor: chartColor(props.color),
                },
            ],
        };
    });

    const chartOptions = computed<ChartOptions<'line'>>(() => {
        // Scale to the series' own range so small changes stay visible
        const min = Math.min(...props.trend);
        const max = Math.max(...props.trend);
        return {
            responsive: true,
            maintainAspectRatio: false,
            animation: reducedMotion ? false : undefined,
            layout: { padding: 4 },
            interaction: { mode: 'index', intersect: false },
            plugins: { legend: { display: false }, tooltip: { enabled: false } },
            scales: {
                x: { display: false },
                y: { display: false, min, max: max > min ? max : min + 1 },
            },
            onHover: (_event, elements) => {
                hoverIndex.value = elements[0]?.index ?? null;
            },
        };
    });

    // Changes that round to zero are shown as neutral
    const isFlat = computed(() => Math.abs(props.delta) < 0.05);
    const isGood = computed(() => (isFlat.value ? null : props.delta > 0 === props.higherIsBetter));
    const deltaIcon = computed<InsightsIconName>(() => (isFlat.value ? 'remove' : props.delta > 0 ? 'arrowUp' : 'arrowDown'));
    const deltaText = computed(() => {
        const sign = isFlat.value ? '±' : props.delta > 0 ? '+' : '−';
        return `${sign}${formatNumber(Math.abs(props.delta), 1)}${props.deltaUnit === 'pp' ? ' pp' : '%'}`;
    });

    const deltaTooltip = computed(() => {
        const count = props.trend.length;
        if (count < 2) return '';
        const previousMonth = props.trendLabels[count - 2];
        const range = `(${props.formatTrendValue(props.trend[count - 2])} → ${props.formatTrendValue(props.trend[count - 1])})`;
        if (isFlat.value) return `No change compared with ${previousMonth} ${range}`;
        const change = props.deltaUnit === 'pp' ? `${deltaText.value.replace(' pp', '')} percentage points` : deltaText.value;
        return `${change} compared with ${previousMonth} ${range}`;
    });

</script>

<template>
    <section class="insights-widget d-flex flex-direction-col gap-xxs">
        <div class="d-flex flex-wrap align-items-start justify-content-between gap-xs">
            <p class="fw-600 m-0-all">{{ label }}</p>
            <span
                v-tooltip.top="deltaTooltip"
                class="insights-kpi-delta d-inline-flex align-items-center gap-xxs text-sm fw-600 text-nowrap"
                :class="{ 'is-good': isGood === true, 'is-bad': isGood === false }"
                tabindex="0"
                :aria-label="deltaTooltip"
            >
                <InsightsIcon :icon="deltaIcon" :size="14" />
                {{ deltaText }}
            </span>
        </div>
        <span class="insights-kpi-value fw-600 text-nowrap text-overflow-ellipsis">{{ value }}</span>
        <div class="insights-kpi-trend p-relative m-xs-t" @mouseleave="hoverIndex = null">
            <Line :data="chartData" :options="chartOptions" :plugins="[crosshairPlugin]" :aria-label="`${label}, last ${trend.length} months`" role="img" />
        </div>
        <p class="insights-kpi-footnote text-sm m-xs-t m-0-b">
            <template v-if="hoverIndex !== null">
                <strong>{{ trendLabels[hoverIndex] }}</strong> ({{ formatTrendValue(trend[hoverIndex]) }})
            </template>
            <template v-else>{{ trendLabels[0] }} – {{ trendLabels[trendLabels.length - 1] }}</template>
        </p>
    </section>
</template>
